import { useEffect, useState } from "react";
import {
  deprovisionClubDomain,
  provisionClubDomain,
  testClubDomain,
  type DnsRecord,
  type DomainCheckResult,
  type DomainProvision,
  type ProvisionStatus,
} from "../lib/api";

const ICON = { pass: "✅", warn: "⚠️", fail: "❌", skipped: "⏭️" } as const;
const LABEL = { dns: "DNS", https: "HTTPS", app: "RC Race HQ" } as const;
const STATUS: Record<ProvisionStatus, string> = {
  validating: "Validating",
  deploying: "Deploying",
  active: "Active",
  failed: "Failed",
  removing: "Removing",
};
const IN_FLIGHT: ProvisionStatus[] = ["validating", "deploying", "removing"];
const REFRESH_MS = 30_000;

/** Per saved club domain: provisioning status with Provision/Remove (admins),
 *  the DNS records to hand the club, and the Test button
 *  (DNS -> HTTPS -> our check file). */
export default function DomainChecks({
  clubId,
  domains,
  lastResults,
  provisioning,
  records,
  canProvision = false,
  onChanged,
}: {
  clubId: string;
  domains: string[];
  lastResults?: Record<string, DomainCheckResult>;
  provisioning?: Record<string, DomainProvision>;
  records?: Record<string, DnsRecord[]>;
  canProvision?: boolean;
  onChanged?: () => void;
}) {
  const [results, setResults] = useState<Record<string, DomainCheckResult>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<Record<string, string>>({});

  // While anything is mid-flight, reload now and then so the status moves
  // without a manual refresh (the backend poller runs every 5 minutes).
  const inFlight = Object.values(provisioning ?? {}).some((p) => IN_FLIGHT.includes(p.status));
  useEffect(() => {
    if (!inFlight || !onChanged) return;
    const t = setInterval(onChanged, REFRESH_MS);
    return () => clearInterval(t);
  }, [inFlight, onChanged]);

  async function act(domain: string, key: string, fn: () => Promise<unknown>) {
    setBusy(`${key}:${domain}`);
    setError(({ [domain]: _, ...rest }) => rest);
    try {
      await fn();
    } catch (err) {
      setError((e) => ({ ...e, [domain]: err instanceof Error ? err.message : "Something went wrong." }));
    } finally {
      setBusy(null);
    }
  }

  const test = (domain: string) =>
    act(domain, "test", async () => {
      const res = await testClubDomain(clubId, domain);
      setResults((r) => ({ ...r, [domain]: res.result }));
    });
  const provision = (domain: string) =>
    act(domain, "provision", async () => {
      await provisionClubDomain(clubId, domain);
      onChanged?.();
    });
  const remove = (domain: string) => {
    if (!confirm(`Remove ${domain} from CloudFront and delete its certificate? The club's site stops working on it.`)) return;
    return act(domain, "remove", async () => {
      await deprovisionClubDomain(clubId, domain);
      onChanged?.();
    });
  };

  if (domains.length === 0) return null;
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "0.25rem 0 0" }}>
      {domains.map((domain) => {
        const result = results[domain] ?? lastResults?.[domain];
        const prov = provisioning?.[domain];
        const recs = records?.[domain] ?? [];
        const showProvision = canProvision && (!prov || prov.status === "failed");
        const showRemove = !!prov && prov.status !== "removing";
        return (
          <li key={domain} style={{ marginBottom: "0.75rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
              <code>{domain}</code>
              <span className="pill" title={prov?.message}>{prov ? STATUS[prov.status] : "Saved only"}</span>
              {result && <span className="pill">{result.ok ? "Working" : "Not working"}</span>}
              {showProvision && (
                <button type="button" disabled={busy !== null} onClick={() => provision(domain)}>
                  {busy === `provision:${domain}` ? "Provisioning…" : prov ? "Provision again" : "Provision"}
                </button>
              )}
              {showRemove && (
                <button type="button" className="secondary" disabled={busy !== null} onClick={() => remove(domain)}>
                  {busy === `remove:${domain}` ? "Removing…" : "Remove"}
                </button>
              )}
              <button type="button" className="secondary" disabled={busy !== null} onClick={() => test(domain)}>
                {busy === `test:${domain}` ? "Testing…" : "Test"}
              </button>
            </div>
            {error[domain] && <p className="err" style={{ margin: "0.25rem 0" }}>{error[domain]}</p>}
            {prov && (
              <div className="muted" style={{ fontSize: 13, marginTop: "0.25rem" }}>
                {prov.message} <span>(updated {new Date(prov.updatedAt).toLocaleString()})</span>
              </div>
            )}
            {prov && prov.status !== "removing" && recs.length > 0 && <DnsRecords records={recs} />}
            {result && (
              <div className="muted" style={{ fontSize: 13 }}>
                {result.steps.map((s) => (
                  <div key={s.step}>
                    {ICON[s.status]} <strong>{LABEL[s.step]}:</strong> {s.message}
                  </div>
                ))}
                <div>Checked {new Date(result.checkedAt).toLocaleString()}</div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** "Add these at your DNS provider" -- everything the club needs, with copy
 *  buttons. The certificate record can go in before the site record, so a
 *  live domain gets its certificate before traffic moves. */
function DnsRecords({ records }: { records: DnsRecord[] }) {
  return (
    <div style={{ fontSize: 13, margin: "0.35rem 0" }}>
      <div className="muted">Records for the club to add at their DNS provider:</div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>For</th>
              <th>Type</th>
              <th>Name</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={`${r.purpose}:${r.name}`}>
                <td>
                  {r.purpose === "certificate" ? "Certificate" : "Site"}
                  {r.addedForYou && (
                    <div className="muted" title="This domain's DNS is in our own Route 53, so we added it">
                      Added for you
                    </div>
                  )}
                </td>
                <td title={r.type === "ALIAS" ? "Bare domains can't be a CNAME: use ALIAS/ANAME, or a Route 53 alias" : undefined}>
                  {r.type === "ALIAS" ? "ALIAS / ANAME" : r.type}
                </td>
                <td>
                  <code>{r.name}</code> <CopyButton text={r.name} />
                </td>
                <td>
                  <code>{r.value}</code> <CopyButton text={r.value} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="link"
      onClick={() =>
        navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
      }
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}
